import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useSelector } from 'react-redux';

import Integrations from "./pages/integrations";
import Users from "./pages/users";
import User from "./pages/user";
import Audit from "./pages/audit";
import Billing from "./pages/billing";
import Suppliers from "./pages/suppliers";
import SupplierCreate from "./pages/supplier/create";
import Beneficiaries from "./pages/beneficiaries";
import BeneficiariesCreate from "./pages/beneficiaries/create";
import Bank from "./pages/bank";
import Portal from "src/pages/organisation/pages/portal";
import PaymentProvider from "src/pages/organisation/pages/intergration/payment-provider";
import PaymentWeights from "src/pages/organisation/pages/intergration/payment-weights";
import CreatePaymentProvider from "src/pages/organisation/pages/intergration/provider-manage/create";
import EditPaymentProvider from "src/pages/organisation/pages/intergration/provider-manage/edit";
import Settings from "src/pages/organisation/pages/settings";
import Crezco from './pages/intergration/crezco';
import Amex from './pages/intergration/amex';
import Traveltek from './pages/intergration/traveltek';

import RedirectRenderer from "src/components/redirect-renderer";
import HasFeature from "src/utils/has-feature";
import HasRole from "src/utils/has-role";
import GroupMenu from "src/components/group-menu";
import GroupContainer from "src/components/group-container";

import {
  ArrowsRightLeftIcon,
  WindowIcon,
  BuildingLibraryIcon,
  FingerPrintIcon,
  Square3Stack3DIcon,
  BuildingOffice2Icon,
  UsersIcon,
  DocumentDuplicateIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import { PROVIDER_CONFIGURATION } from "src/config/providers";

export const getRoutes = (account, features) => [
  { name: 'Audit Trail', description: 'View the audit trail of user actions', path: '/organisation/audit', check: HasRole(account, ['organisation:audit']) && HasFeature(features, ['audit']) },
  { name: 'Beneficiaries', description: 'Manage beneficiary accounts for your organisation', path: '/organisation/beneficaries', check: HasRole(account, ['beneficiaries:manage']) && HasFeature(features, ['payouts']) },
  { name: 'Billing', description: 'Manage your organisation billing and invoices', path: '/organisation/billing', check: HasRole(account, ['billing:view']) },
  { name: 'Integrations', description: 'Manage the integrations for your organisation', path: '/organisation/integrations', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Payment Weights', description: 'Manage payment weights for your providers', path: '/organisation/integrations/payment-weights', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Crezco Integration', description: 'Take payments via Open Banking with Crezco', path: '/organisation/integrations/crezco', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Amex Integration', description: 'Reconcile Transactions and Batches from AMEX', path: '/organisation/integrations/amex', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Traveltek Integration', description: 'Synchronise payment receipts with Traveltek', path: '/organisation/integrations/traveltek', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Portal', description: 'Configure the Felloh Portal for your organisation', path: '/organisation/portal', check: HasRole(account, ['organisation:integrations']) },
  { name: 'Bank Details', description: 'Manage AISP bank feeds', path: '/organisation/bank', check: HasFeature(features, ['aisp']) && HasRole(account, ['open-banking:manage']) },
  { name: 'Organisation Settings', description: 'Manage the settings for your organisation', path: '/organisation/settings', check: HasRole(account, ['organisation:management']) },
  { name: 'Suppliers', description: 'Manage the suppliers for your organisation', path: '/organisation/suppliers', check: HasRole(account, ['suppliers:manage']) && HasFeature(features, ['suppliers']) },
  { name: 'Users', description: 'Manage the users in your organisation', path: '/organisation/users', check: HasRole(account, ['users:manage']) && HasFeature(features, ['users']) },
];

const Layout = ({ children }) => {
  const { organisation: { features }, account: { account } } = useSelector((state) => state);

  const items = [
    { name: 'Audit Trail', description: 'View the audit trail of user actions for your organisation', icon: FingerPrintIcon, path: '/organisation/audit', check: HasRole(account, ['organisation:audit']) && HasFeature(features, ['audit']) },
    { name: 'Beneficaries', description: 'Manage the beneficary accounts for your organisation funds', icon: BuildingLibraryIcon, path: '/organisation/beneficaries', check: HasRole(account, ['beneficiaries:manage']) && HasFeature(features, ['payouts']) },
    { name: 'Billing', description: 'Manage your organisation billing and invoices', icon: DocumentDuplicateIcon, path: '/organisation/billing', check: HasRole(account, ['billing:view']) },
    { name: 'Users', description: 'Manage the users in your felloh organisation',icon: UsersIcon, path: '/organisation/users', check: HasRole(account, ['users:manage']) && HasFeature(features, ['users']) },
    { name: 'Integrations', description: 'Manage the integrations for your organisation', icon: Square3Stack3DIcon, path: '/organisation/integrations', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Settings', description: 'Manage the settings for your organisation', icon: WrenchScrewdriverIcon, path: '/organisation/settings', check: HasRole(account, ['organisation:management']) },
    { name: 'Suppliers', description: 'Manage the suppliers for your organisation', icon: BuildingOffice2Icon, path: '/organisation/suppliers', check: HasRole(account, ['suppliers:manage']) && HasFeature(features, ['suppliers']) },

    { name: 'User', description: 'Manage a user of your organisation', visible: false, parent: 'Users', icon: UsersIcon, path: '/organisation/user/:id', check: HasRole(account, ['users:manage']) && HasFeature(features, ['users']) },
    { name: 'User Roles', description: 'Manage the roles of a user in your organisation', visible: false, parent: 'Users', icon: UsersIcon, path: '/organisation/user/:id/roles', check: HasRole(account, ['users:manage']) && HasFeature(features, ['users']) },
    { name: 'User Organisations', description: 'Manage the organisations a user is part of', visible: false, parent: 'Users', icon: UsersIcon, path: '/organisation/user/:id/organisations', check: HasRole(account, ['users:manage']) && HasFeature(features, ['users']) },

    { name: 'Banking Integrations', description: 'Manage AISP bank feeds', parent: 'Integrations', visible: false, icon: Square3Stack3DIcon, path: '/organisation/bank', check: HasFeature(features, ['aisp']) && HasRole(account, ['open-banking:manage']) },
    { name: 'Felloh Portal Configuration', description: 'Configure the Felloh Portal for your organisation', parent: 'Integrations', visible: false, icon: WindowIcon, path: '/organisation/portal', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Payment Weighting(s)', description: 'Manage the payment weights for your payment providers', icon: ScaleIcon, parent: 'Integrations', visible: false, path: '/organisation/integrations/payment-weights', check: HasRole(account, ['organisation:integrations']) }, 
    { name: 'Crezco', description: 'Take payments via Open Banking with Crezco', parent: 'Integrations', icon: ArrowsRightLeftIcon, visible: false, path: '/organisation/integrations/crezco', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Amex', description: 'Reconcile Transactions and Batches from AMEX to Felloh', parent: 'Integrations', icon: ArrowsRightLeftIcon, visible: false, path: '/organisation/integrations/amex', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Traveltek', description: 'Synchronise your payment receipts with Traveltek', parent: 'Integrations', icon: ArrowsRightLeftIcon, visible: false, path: '/organisation/integrations/traveltek', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Payment Provider', description: 'Manage a payment provider for your organisation', parent: 'Integrations', visible: false, icon: ArrowsRightLeftIcon, path: '/organisation/integrations/provider/:id/create', check: HasRole(account, ['organisation:integrations']) },
    { name: 'Edit Payment Provider', description: 'Edit a payment provider for your organisation', parent: 'Integrations', visible: false, icon: ArrowsRightLeftIcon, path: '/organisation/integrations/provider/:id/edit', check: HasRole(account, ['organisation:integrations']) },
  ];

  Object.keys(PROVIDER_CONFIGURATION).forEach((key) => {
    const provider = PROVIDER_CONFIGURATION[key];

    if (provider.auto_routing === true) {
      items.push({
        name: `${provider.display_name} Integration`,
        parent: 'Integrations',
        icon: ArrowsRightLeftIcon,
        visible: false,
        path: provider.manage_path,
        check: HasRole(account, ['organisation:integrations']),
      });
    }
  });

  return (
    <div id="content">
      <GroupMenu items={items} title="Organisation" />
      <GroupContainer>
        {children}
      </GroupContainer>
    </div>
  )
};

const BasePathRenderer = () => {
  const { organisation: { features }, account: { account } } = useSelector((state) => state);

  let path = '/';

  if (HasRole(account, ['users:manage']) && HasFeature(features, ['users'])) {
    path = `/organisation/users`;
  } else if (HasRole(account, ['organisation:management'])) {
    path = `/organisation/settings`;
  } else if (HasRole(account, ['organisation:integrations'])) {
    path = `/organisation/integrations`;
  } else if (HasRole(account, ['organisation:audit'])) {
    path = `/organisation/audit`;
  } else if (HasRole(account, ['billing:view'])) {
    path = `/organisation/billing`;
  } else if (HasRole(account, ['suppliers:manage']) && HasFeature(features, ['suppliers'])) {
    path = `/organisation/suppliers`;
  } else if (HasRole(account, ['beneficiaries:manage']) && HasFeature(features, ['payouts'])) {
    path = `/organisation/beneficaries`;
  } else if (HasFeature(features, ['aisp']) && HasRole(account, ['open-banking:manage'])) {
    path = `/organisation/bank`;
  }

  return (
    <Route exact path="/organisation" component={(props) => {
      return (
        <Redirect to={path} />
      )
    }}
    />
  );
};

const redirects = [
  { from: '/integrations', to: '/organisation/integrations' },
  { from: '/users', to: '/organisation/users' },
  { from: '/users/:id', to: '/organisation/user/:id' },
  { from: '/users/:id/roles', to: '/organisation/user/:id/roles' },
  { from: '/users/:id/organisations', to: '/organisation/user/:id/organisations' },
  { from: '/audit', to: '/organisation/audit' },
  { from: '/billing', to: '/organisation/billing' },
  { from: '/suppliers', to: '/organisation/suppliers' },
  { from: '/supplier/create', to: '/organisation/supplier/create' },
  { from: '/beneficiaries', to: '/organisation/beneficiaries' },
  { from: '/beneficiaries/create', to: '/organisation/beneficiaries/create' },
];

const Payments = () => {
  const { organisation: { features, is_admin }, account: { account } } = useSelector((state) => state);

  if (account?.id) {
    return (
      <React.Fragment>

        {RedirectRenderer({ redirects })}
        {BasePathRenderer()}

        {HasRole(account, ['organisation:integrations']) && (
          <React.Fragment>
            <Route exact path={`/organisation/integrations`} component={() => <Layout><Integrations /></Layout>} />
            <Route exact path={`/organisation/portal`} component={() => <Layout><Portal /></Layout>} />
            <Route exact path={`/organisation/integrations/payment-weights`} component={() => <Layout><PaymentWeights /></Layout>} />

            { Object.keys(PROVIDER_CONFIGURATION).map((key) => {
              const provider = PROVIDER_CONFIGURATION[key];

              if (provider.auto_routing === true) {
                return (
                  <Route key={key} exact path={provider.manage_path} component={() =>
                    <Layout><PaymentProvider providerID={key} providerName={provider.name} /></Layout>}
                  />
                );
              } else {
                return <React.Fragment key={key} />;
              }
            })}

            <Route exact path={`/organisation/integrations/provider/:id/create`} component={() => <Layout><CreatePaymentProvider /></Layout>} />
            <Route exact path={`/organisation/integrations/provider/:id/edit`} component={() => <Layout><EditPaymentProvider /></Layout>} />

          </React.Fragment>
        )}

        {HasRole(account, ['organisation:integrations']) && (
          <React.Fragment>
            <Route exact path={`/organisation/integrations/crezco`} component={() => <Layout><Crezco providerID={'3337ae9b-0120-4fa5-b797-e0ec5f33d8d0'} /></Layout>} />
            <Route exact path={`/organisation/integrations/amex`} component={() => <Layout><Amex/></Layout>} />
            <Route exact path={`/organisation/integrations/traveltek`} component={() => <Layout><Traveltek/></Layout>} />
          </React.Fragment>
        )}

        {HasFeature(features, ['aisp']) && HasRole(account, ['open-banking:manage']) && (
          <React.Fragment>
            <Route exact path={`/organisation/bank`} component={() => <Layout><Bank /></Layout>} />
          </React.Fragment>
        )}

        {HasRole(account, ['users:manage']) && HasFeature(features, ['users']) && (
          <React.Fragment>
            <Route exact path={`/organisation/users`} component={() => <Layout><Users /></Layout>} />
            <Route exact path={`/organisation/user/:id`} component={() => <Layout><User /></Layout>} />
            <Route exact path={`/organisation/user/:id/roles`} component={() => <Layout><User /></Layout>} />
            <Route exact path={`/organisation/user/:id/organisations`} component={() => <Layout><User /></Layout>} />
          </React.Fragment>
        )}

        {HasRole(account, ['organisation:audit']) && HasFeature(features, ['audit']) && (
          <Route exact path={`/organisation/audit`} component={() => <Layout><Audit /></Layout>} />
        )}

        {HasRole(account, ['organisation:management']) && (
          <Route exact path={`/organisation/settings`} component={() => <Layout><Settings /></Layout>} />
        )}

        {HasRole(account, ['billing:view']) && (
          <Route exact path={`/organisation/billing`} component={() => <Layout><Billing /></Layout>} />
        )}

        {HasRole(account, ['suppliers:manage']) && HasFeature(features, ['suppliers']) && (
          <React.Fragment>
            <Route exact path={`/organisation/suppliers`} component={() => <Layout><Suppliers /></Layout>} />
            <Route exact path={`/organisation/supplier/create`} component={() => <Layout><SupplierCreate /></Layout>} />
          </React.Fragment>
        )}

        {
          HasRole(account, ['beneficiaries:manage']) && HasFeature(features, ['payouts']) && (
            <React.Fragment>
              <Route exact path={`/organisation/beneficaries`} component={() => <Layout><Beneficiaries /></Layout>} />
            </React.Fragment>
          )}

                  {
          HasRole(account, ['beneficiaries:manage']) && is_admin === true && HasFeature(features, ['payouts']) && (
            <React.Fragment>
              <Route exact path={`/organisation/beneficaries/create`} component={() => <Layout><BeneficiariesCreate /></Layout>} />
            </React.Fragment>
          )}


      </React.Fragment>
    );
  }

  return <React.Fragment />;
};

export default Payments;
